import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { cn } from '../../../utils/cn';
import { Icon } from '../Icon/Icon';
import { Portal } from '../Portal/Portal';

type ToastTone = 'success' | 'error' | 'info';
interface ToastItem {
  id: number;
  message: ReactNode;
  tone: ToastTone;
}

export interface ToastApi {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);
let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: number) => setToasts((t) => t.filter((x) => x.id !== id)), []);
  const show = useCallback(
    (message: string, tone: ToastTone = 'success', ms = 3500) => {
      const id = nextId++;
      setToasts((t) => [...t.slice(-2), { id, message, tone }]);
      setTimeout(() => dismiss(id), ms);
    },
    [dismiss],
  );

  const api = useMemo(
    () => ({
      success: (m: string) => show(m, 'success'),
      error: (m: string) => show(m, 'error', 5000),
      info: (m: string) => show(m, 'info'),
    }),
    [show],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <Portal>
        <div className="pointer-events-none fixed inset-x-0 bottom-20 z-[70] flex flex-col items-center gap-2 px-4 lg:bottom-6 lg:items-end lg:px-6" role="status" aria-live="polite">
          {toasts.map((t) => (
            <div
              key={t.id}
              className={cn(
                'pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-xl px-4 py-3 text-sm shadow-pop animate-pop',
                t.tone === 'error' ? 'bg-red-600 text-white' : 'bg-navy-800 text-white',
              )}
            >
              <Icon
                name={t.tone === 'error' ? 'alert' : t.tone === 'info' ? 'info' : 'checkCircle'}
                size={20}
                className={cn('mt-px shrink-0', t.tone === 'error' ? 'text-white' : 'text-emerald-400')}
              />
              <span className="flex-1">{t.message}</span>
              <button type="button" onClick={() => dismiss(t.id)} aria-label="Dismiss" className="-mr-1 rounded p-0.5 text-white/70 hover:text-white">
                <Icon name="x" size={16} />
              </button>
            </div>
          ))}
        </div>
      </Portal>
    </ToastContext.Provider>
  );
}

export const useToast = (): ToastApi => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
};
export default ToastProvider;
